import { mark, measure, TIMINGS } from './timings';
import { resolve, type ResolverResponse } from './resolve';
import type { LoaderConfiguration } from './types';

declare global {
  interface Window {
    AlgoliaExperiences?: {
      run: (config?: Record<string, unknown>) => void;
      dispose: () => void;
    };
    __ALGOLIA_EXPERIENCES_TOOLBAR_CONFIG__?: {
      appId: string;
      apiKey: string;
      experienceId: string;
      env?: string;
    };
  }
}

function preconnect(origin: string, anonymous: boolean): void {
  const suitable = Array.from(document.querySelectorAll('link')).some(
    (link) => {
      if (
        !(link instanceof HTMLLinkElement) ||
        !link.rel.toLowerCase().split(/\s+/).includes('preconnect')
      ) {
        return false;
      }

      try {
        if (new URL(link.href, document.baseURI).origin !== origin) {
          return false;
        }
      } catch {
        return false;
      }

      const corsMode = link.getAttribute('crossorigin');
      return anonymous
        ? corsMode !== null &&
            (corsMode === '' || corsMode.toLowerCase() === 'anonymous')
        : corsMode === null;
    }
  );

  if (suitable) {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  if (anonymous) {
    link.crossOrigin = 'anonymous';
  }
  document.head.appendChild(link);
}

/**
 * Main loader function that orchestrates the loading process:
 *
 * 1. Calls the resolver to get the runtime bundle URL
 * 2. Injects the runtime script into the page
 * 3. Calls the runtime's run function with optional config
 *
 * Returns the resolved bundle URL so callers (e.g. preview entry) can
 * derive sibling asset URLs from it.
 */
export async function load(config: LoaderConfiguration): Promise<string> {
  mark(TIMINGS.start);

  preconnect('https://experiences.cdn.algolia.com', false);
  preconnect(
    config.env === 'beta'
      ? 'https://experiences-beta.algolia.com'
      : 'https://experiences.algolia.com',
    true
  );

  const endResolve = measure(TIMINGS.resolve);
  let bundle: ResolverResponse;
  try {
    bundle = await resolve(config);
  } finally {
    endResolve();
  }

  const runtimeUrl = new URL(bundle.bundleUrl);
  runtimeUrl.searchParams.set('appId', config.appId);
  runtimeUrl.searchParams.set('apiKey', config.apiKey);
  runtimeUrl.searchParams.set('experienceId', config.experienceId);
  if (config.env) {
    runtimeUrl.searchParams.set('env', config.env);
  }
  if (config.preview) {
    runtimeUrl.searchParams.set('preview', 'true');
  }

  // TODO: Load CSS via <link> once served from a CDN with proper MIME type.
  // Currently, CSS is inlined in the runtime JS bundle (see injectCssPlugin in
  // packages/runtime/tsdown.config.ts).

  const endRuntimeLoad = measure(TIMINGS.runtimeLoad);
  const script = document.createElement('script');
  script.src = runtimeUrl.toString();
  script.onload = () => {
    endRuntimeLoad();
    window.AlgoliaExperiences?.run(config.runtimeConfig);
  };
  script.onerror = () => {
    endRuntimeLoad();
    console.error(
      new Error(
        `[@algolia/experiences] Failed to load bundle: ${bundle.bundleUrl}`
      )
    );
  };

  document.head.appendChild(script);

  return bundle.bundleUrl;
}
